import { useMemo, useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import type { Movie } from "@/types/movie";
import { filterMovies, type FilterOptions } from "@/lib/movieList";
import MovieCard from "@/components/feature/MovieCard";
import FilterPanel from "@/pages/category/components/FilterPanel";

const list = movies as Movie[];

export default function Category() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const routeType = params.type === "movie" || params.type === "tv" ? params.type : "all";

  const initialGenre = searchParams.get("genre") ?? "全部";
  const [options, setOptions] = useState<FilterOptions>({
    category: routeType,
    genre: initialGenre,
    region: "全部",
    year: "all",
    sort: "heat",
  });
  const [visible, setVisible] = useState(8);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    setOptions((prev) => ({ ...prev, category: routeType }));
    setVisible(8);
  }, [routeType]);

  const filtered = useMemo(() => filterMovies(list, options), [options]);

  useEffect(() => {
    setVisible(8);
  }, [options]);

  const updateOptions = (next: Partial<FilterOptions>) => {
    setOptions((prev) => ({ ...prev, ...next }));
  };

  const resetOptions = () => {
    setOptions({ category: routeType, genre: "全部", region: "全部", year: "all", sort: "heat" });
  };

  const hasActiveFilter =
    options.genre !== "全部" || options.region !== "全部" || options.year !== "all" || options.sort !== "heat";

  const pageTitle = routeType === "movie" ? "电影分类" : routeType === "tv" ? "电视剧分类" : "全部影视分类";
  const pageDesc =
    routeType === "movie"
      ? "按地区、年份、题材与热度筛选最新电影资源，快速找到你想看的高分佳作与院线新片。"
      : routeType === "tv"
      ? "按地区、年份、题材与热度筛选热门电视剧全集，追剧更省心。"
      : "按影视类型、地区、年份、题材与热度综合筛选，快速定位最新影视资源。";

  useEffect(() => {
    document.title = `${pageTitle} - 97影院`;
  }, [pageTitle]);

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
      <nav className="mb-5 flex items-center gap-1.5 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">{pageTitle}</span>
      </nav>

      <div className="mb-8">
        <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">{pageTitle}</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-500">{pageDesc}</p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
        <aside className="w-full shrink-0 lg:w-72">
          <button
            type="button"
            onClick={() => setPanelOpen((v) => !v)}
            className="mb-3 flex w-full items-center justify-between rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 lg:hidden"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line text-lg text-primary-600" />
              筛选条件
            </span>
            <i className={panelOpen ? "ri-arrow-up-s-line text-lg" : "ri-arrow-down-s-line text-lg"} />
          </button>
          <div className={`${panelOpen ? "block" : "hidden"} lg:sticky lg:top-20 lg:block`}>
            <FilterPanel options={options} onChange={updateOptions} onReset={resetOptions} hideCategory={routeType !== "all"} />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-semibold text-accent-600">{filtered.length}</span> 部影视作品
            </p>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={resetOptions}
                className="flex items-center gap-1 whitespace-nowrap rounded-full border border-accent-300 bg-accent-50 px-3 py-1.5 text-xs font-medium text-accent-700 transition-colors hover:bg-accent-100"
              >
                <i className="ri-close-line" />
                清空筛选
              </button>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-2-line text-3xl text-foreground-400" />
              </span>
              <p className="mt-4 text-base font-medium text-foreground-800">没有找到符合条件的影视作品</p>
              <p className="mt-1 text-sm text-foreground-500">试试放宽筛选条件，或清空全部筛选</p>
              <button
                type="button"
                onClick={resetOptions}
                className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                清空筛选条件
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 md:gap-5">
                {filtered.slice(0, visible).map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>

              {visible < filtered.length && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + 8)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-7 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                  >
                    <i className="ri-add-line text-base" />
                    加载更多（剩余 {filtered.length - visible} 部）
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}